import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getLocalMemberRole, ROLE } from '../members';
import { Icon } from '../ui';
import { refetchLatestChat, removeChatList, updateBeforeChat, setIsLive, swithToLiveMessage } from './action';
import ChatItemContainer from './ChatItemContainer';
import { getChatIds, getIsLive, getIsWindowSlid, getlastMessage } from './functions';
// 바닥 근처 판정 임계값(px). 이 이내면 '바닥에 있음'으로 보고 새 메시지를 자동 추종한다.
const BOTTOM_THRESHOLD = 48;
// 채팅 발신자 표시명 — 채팅 목록(ChatItemContainer)과 동일 규칙.
// admin(관리자)은 본인이 admin 이 아니면 익명 처리(role.admin),
// 진행자(manager)는 전 영역 실명 마스킹('조교', 발신자 본인 화면 포함),
// 그 외는 닉네임의 '(아이디)' 제거.
const resolveChatSenderName = (msg, localRole, t) => {
    if (!msg)
        return '';
    if (msg.fromRole === 'admin' && localRole !== ROLE.ADMIN)
        return t('role.admin');
    if (msg.fromRole === ROLE.MANAGER)
        return t('role.assistant');
    return msg.fromNickname?.split('(')[0] || '';
};
const ChatBodyContainer = React.memo(({ showSendChat }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const chatItems = useSelector((state) => getChatIds(state), shallowEqual);
    const lastMessage = useSelector((state) => getlastMessage(state));
    const isLive = useSelector((state) => getIsLive(state));
    const localRole = useSelector((state) => getLocalMemberRole(state));
    // 위로 페이징으로 표시 윈도우가 슬라이드(최신쪽 폐기)된 상태 — 바닥 복귀 시 복원 방식 결정
    const windowSlid = useSelector((state) => getIsWindowSlid(state));
    const [newMessage, setNewMessage] = useState(null); // 위로 읽는 중 새 메시지 도착 시 배너
    const scrollRef = useRef(null);
    const initLoadRef = useRef(true); // 최초 로드 여부
    const atBottomRef = useRef(true); // 사용자가 바닥 근처인지 (스크롤로 갱신, 동기적)
    const prevItemsRef = useRef([]); // 직전 표시 메시지 id 목록
    const prevLastUuidRef = useRef(null); // 직전 마지막 메시지 uuid
    // 이전(과거) 페이지 로드 시 위치 유지용 스냅샷.
    // (uuid 앵커 방식은 근접 중복제거로 앵커가 사라지면 복원이 통째로 스킵되고 stale 앵커가
    // 바닥 유지까지 영구 차단하던 결함 → scrollHeight 증가분 보정 방식으로 교체)
    const pendingRestoreRef = useRef(null);
    const loadingOlderRef = useRef(false); // 이전 메시지 로드 중복 요청 방지
    const reachedOldestRef = useRef(false); // 히스토리 최상단 도달(빈 응답) — 재요청 중단
    const refetchingLatestRef = useRef(false); // 최신 재조회 중복 방지
    const lastScrollTopRef = useRef(0); // 직전 scrollTop (사용자 위로-스크롤 판정용)
    const scrollToBottom = useCallback(() => {
        const el = scrollRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, []);
    // 바닥 이동 예약. ★듀얼모니터는 별도 창(react-new-window)이라 opener(메인 창)의
    // requestAnimationFrame 은 메인 창이 가려지거나 최소화되면 스로틀/정지되어 새 메시지
    // 자동 바닥 이동이 실행되지 않는다 — 스크롤 대상 요소가 속한 창(듀얼 창)의 rAF 로 예약한다
    // (line 157 ownerDocument / ResizeObserver 처리와 동일 원칙). 기본 창에선 defaultView===window
    // 라 동작 불변.
    const scheduleScrollToBottom = useCallback(() => {
        const win = scrollRef.current?.ownerDocument?.defaultView || window;
        const raf = win.requestAnimationFrame || window.requestAnimationFrame;
        raf(scrollToBottom);
    }, [scrollToBottom]);
    // 바닥 복귀(최신 추종 복원): 윈도우가 슬라이드해 최신 블록이 폐기됐으면 이어붙이기
    // (SWITCH_TO_LIVE — 폐기 구간이 공백으로 남음) 대신 서버 기준 최신 페이지로 교체 조회한다.
    const jumpToLatest = useCallback(() => {
        if (windowSlid) {
            if (!refetchingLatestRef.current) {
                refetchingLatestRef.current = true;
                dispatch(refetchLatestChat());
            }
        }
        else {
            dispatch(swithToLiveMessage());
        }
    }, [windowSlid, dispatch]);
    // 언마운트 시 표시 목록 정리
    useEffect(() => {
        return () => {
            dispatch(removeChatList());
        };
    }, []);
    // 스크롤 위치 추적: 바닥/최상단 판정 + 이전 메시지 로드 + 바닥 복귀 시 live 전환
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const prevTop = lastScrollTopRef.current;
        const curTop = el.scrollTop;
        lastScrollTopRef.current = curTop;
        const nearBottom = el.scrollHeight - el.clientHeight - curTop < BOTTOM_THRESHOLD;
        // ★사용자가 "실제로 위로 올렸는지"를 스크롤 방향으로 판정한다. 새 메시지가 빠르게 쌓여
        // scrollToBottom 의 scroll 이벤트가 뒤늦게 처리될 때는 scrollTop 이 줄지 않으므로(콘텐츠만
        // 늘어 바닥에서 밀린 것) 추종을 끄지 않는다 — rapid 채팅 자동 스크롤 멈춤 방지.
        // 1px 여유: 서브픽셀 흔들림은 거르되 트랙패드 저속 드래그(프레임당 1-2px)는 인식.
        const userScrolledUp = prevTop - curTop >= 1;
        // 최상단을 벗어나면 이전-로드 잠금과 복원 스냅샷을 함께 정리 — stale 스냅샷이
        // 이후 바닥 유지/복원을 왜곡하지 않도록 한다.
        if (curTop > BOTTOM_THRESHOLD) {
            loadingOlderRef.current = false;
            pendingRestoreRef.current = null;
        }
        // 최상단 도달 → 이전(과거) 메시지 로드. 위치 유지를 위해 현재 스크롤 높이를 스냅샷.
        if (curTop <= 0 && !loadingOlderRef.current && !reachedOldestRef.current && chatItems.length > 0) {
            loadingOlderRef.current = true;
            pendingRestoreRef.current = { prevScrollHeight: el.scrollHeight, prevScrollTop: curTop };
            dispatch(updateBeforeChat(chatItems[0]));
        }
        if (nearBottom) {
            // 바닥 복귀 → 최신 추종 복원(슬라이드 시 재조회 / 아니면 live 전환)하고 배너 제거
            atBottomRef.current = true;
            if (!isLive || windowSlid)
                jumpToLatest();
            if (newMessage)
                setNewMessage(null);
        }
        else if (userScrolledUp) {
            // 사용자가 직접 위로 올림 → 새 메시지가 화면을 밀지 않도록 추종 중단
            atBottomRef.current = false;
            if (isLive)
                dispatch(setIsLive(false));
        }
        // else: 바닥 아님 + 사용자 위로 안 올림(콘텐츠 증가로 밀린 상태) → 추종 유지
    }, [chatItems, isLive, dispatch, newMessage, jumpToLatest, windowSlid]);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);
    // 표시 메시지(chatItems) 변경 처리: 최초 로드 → 바닥, 이전 메시지 로드 → 위치 유지.
    // useLayoutEffect: 복원 전에 최상단(scrollTop=0) 프레임이 그려졌다 스냅되는 플리커 제거.
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el || chatItems.length === 0) {
            prevItemsRef.current = chatItems;
            return;
        }
        // (1) 최초 로드: 바닥으로 이동
        if (initLoadRef.current) {
            scheduleScrollToBottom();
            initLoadRef.current = false;
            prevItemsRef.current = chatItems;
            return;
        }
        // (2) 이전(과거) 페이지 로드 완료(최상단이 바뀜): 보던 위치 유지.
        // ①생존 앵커 우선 — isUp 트림은 최신쪽만 지우므로 직전 최상단(old[0])은 거의 항상
        // 생존하고, offsetTop 복원은 트림(scrollHeight 감소)·중복제거와 무관하게 정확하다.
        // (순수 scrollHeight 델타 보정은 병합+트림이 상쇄되는 정상 페이징에서 체계적으로
        // 과소보정돼 매 페이지 최상단으로 점프하는 결함이 있음)
        // ②앵커 전멸 시에만 델타 보정 폴백.
        const pending = pendingRestoreRef.current;
        if (pending && chatItems[0] !== prevItemsRef.current[0]) {
            let restored = false;
            const curSet = new Set(chatItems);
            const doc = el.ownerDocument || document;
            for (const uuid of prevItemsRef.current) {
                if (!curSet.has(uuid))
                    continue;
                const anchorEl = doc.querySelector(`[data-uuid="${uuid}"]`);
                if (anchorEl) {
                    el.scrollTop = anchorEl.offsetTop;
                    restored = true;
                }
                break; // 첫 생존 uuid 기준 — DOM 미발견이면 폴백
            }
            if (!restored) {
                el.scrollTop = Math.max(0, pending.prevScrollTop + (el.scrollHeight - pending.prevScrollHeight));
            }
            lastScrollTopRef.current = el.scrollTop;
            pendingRestoreRef.current = null;
            loadingOlderRef.current = false;
            prevItemsRef.current = chatItems;
            return;
        }
        // (3) 바닥 추종 중이면 목록이 늘어날 때마다 바닥을 유지한다. lastMessage effect 의 타이밍에만
        // 의존하지 않고 표시목록 변경마다 rAF 로 레이아웃 확정 후 스크롤 → rapid 채팅에서도 안정적으로
        // 바닥을 따라간다. (이전 메시지 로드 중이 아닐 때만)
        if (atBottomRef.current && !pendingRestoreRef.current) {
            scheduleScrollToBottom();
        }
        prevItemsRef.current = chatItems;
    }, [chatItems, scrollToBottom]);
    // 히스토리 로드 완료 신호(성공/빈 응답/실패) — 잠금·스냅샷 정리.
    // 빈 응답/실패 시에는 chatItems 가 안 변해 위 effect 가 돌지 않으므로 여기서 정리해야
    // 잠금이 걸린 채 남거나(top 로드 불능) stale 스냅샷이 바닥 유지를 차단하는 것을 막는다.
    useEffect(() => {
        const onSettled = ({ isUp, isFirst, count, discarded }) => {
            if (isFirst) {
                // 최신 페이지 교체 로드 완료(채팅 오픈/최신 재조회)
                reachedOldestRef.current = false;
                refetchingLatestRef.current = false;
                return;
            }
            if (isUp) {
                loadingOlderRef.current = false;
                if (count === 0) {
                    // 히스토리 최상단 도달 — 빈 응답 반복 요청 방지
                    reachedOldestRef.current = true;
                    pendingRestoreRef.current = null;
                }
                if (count < 0 || discarded) {
                    // 로드 실패/가드 폐기 — stale 스냅샷 정리(다음 top 도달 시 재시도)
                    pendingRestoreRef.current = null;
                }
            }
        };
        APP.eventManager.subscribe('CHAT_HISTORY_SETTLED', onSettled);
        return () => APP.eventManager.unsubscribe('CHAT_HISTORY_SETTLED', onSettled);
    }, []);
    // 새 메시지(lastMessage 변경) 처리: 바닥 추종 중/내 메시지면 바닥으로, 위로 읽는 중이면 배너
    useEffect(() => {
        if (!lastMessage)
            return;
        if (prevLastUuidRef.current === lastMessage.uuid)
            return; // 동일 메시지 재실행 방지
        const isFirst = prevLastUuidRef.current === null;
        prevLastUuidRef.current = lastMessage.uuid;
        if (isFirst)
            return; // 최초 1회는 초기 로드 effect 가 바닥 처리
        // 발신 에코(낙관적 항목의 키 교체)는 새 메시지가 아니다 — 전송 직후 위로 스크롤한
        // 사용자가 에코 도착 시 바닥으로 끌려가지 않도록 스크롤/배너를 트리거하지 않는다.
        if (lastMessage.isEcho)
            return;
        if (lastMessage.isMe || atBottomRef.current) {
            // 내가 보낸 메시지이거나 바닥 근처 → 최신으로 따라가기.
            // ★윈도우가 슬라이드된 상태면 isLive 여부와 무관하게 jumpToLatest(재조회) —
            // 이어붙이기(SWITCH_TO_LIVE)나 슬라이드 윈도우에의 직접 append 는 폐기 구간을
            // 공백으로 실체화한다(스티치 갭 재생산 방지).
            if (!isLive || windowSlid)
                jumpToLatest();
            setNewMessage(null);
            scheduleScrollToBottom();
        }
        else {
            // 위에서 과거 메시지를 읽는 중 → 새 메시지 배너 표시(자동 스크롤하지 않음)
            setNewMessage(lastMessage);
        }
    }, [lastMessage, isLive, dispatch, scrollToBottom, windowSlid, jumpToLatest]);
    // 채팅 패널 재표시(손들기/참석자 등 다른 화면 갔다 복귀) → 최신 추종 복원 + 바닥
    useEffect(() => {
        if (!showSendChat)
            return;
        if (!isLive || windowSlid)
            jumpToLatest();
        atBottomRef.current = true;
        setNewMessage(null);
        // live 전환에 따른 목록 변경 및 레이아웃 반영 후 바닥으로
        const timer = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timer);
    }, [showSendChat]);
    // 컨테이너 리사이즈(모바일 키보드 등) 시 바닥에 있었으면 바닥 유지.
    // ★듀얼모니터는 별도 창(react-new-window)이라 opener 의 window.ResizeObserver 로 듀얼 창
    // 요소를 관찰하면 콜백이 발화하지 않는다 — 관찰 대상 요소가 속한 창의 ResizeObserver 를 쓴다
    // (line 157 ownerDocument 처리와 동일 원칙). 기본 창에선 defaultView===window 라 동작 불변.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const win = el.ownerDocument?.defaultView || window;
        const RO = win.ResizeObserver || window.ResizeObserver;
        if (!RO)
            return;
        const resizeObserver = new RO(() => {
            if (atBottomRef.current)
                scrollToBottom();
        });
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
    }, [scrollToBottom]);
    const renderChatList = useMemo(() => {
        return chatItems.map((uuid) => (React.createElement(ChatItemContainer, { key: uuid, uuid: uuid })));
    }, [chatItems]);
    return (React.createElement("div", { id: "chat_body_scroll", ref: scrollRef, style: {
            height: '100%',
            overflowY: 'scroll',
            touchAction: 'pan-y',
            // iPad: 채팅 스크롤이 문서로 전파(scroll chaining)돼 화면 전체가 밀리는(늘어나는) 것 차단.
            // visual<layout 뷰포트(오토필 바 ≈79px)에서 WebKit 240860 로 발생 → contain 으로 전파 차단(기하 무변경).
            overscrollBehavior: 'contain',
        } },
        renderChatList,
        newMessage &&
            React.createElement("div", { className: "message-banner-max-width-wrapper", onClick: () => {
                    jumpToLatest();
                    setNewMessage(null);
                    atBottomRef.current = true;
                    setTimeout(scrollToBottom, 50);
                } },
                React.createElement("div", { className: "message-banner-max-width" },
                    React.createElement("div", { className: "name" }, resolveChatSenderName(newMessage, localRole, t)),
                    React.createElement("div", { className: "contents" }, newMessage?.message),
                    React.createElement(Icon, { icon: "subtract", size: 16, fill: "#fff" })))));
});
export default ChatBodyContainer;
